import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertTriangle, Building2, Mail, MapPin, Pencil, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useVendor, useVendorContacts } from "@/features/vendors/api";
import {
  GST_TREATMENTS,
  PAYMENT_TERMS,
  VENDOR_TYPES,
  formatMoney,
  labelFor,
} from "@/features/vendors/constants";
import { useVendorId } from "@/features/vendors/detail/use-vendor-id";

export function VendorProfilePage() {
  const vendorId = useVendorId();
  const { data: vendor } = useVendor(vendorId);
  const { data: contacts = [] } = useVendorContacts(vendorId);

  if (!vendor) return null;

  const primary = contacts.find((c) => c.is_primary) ?? contacts[0];
  const gaps: string[] = [];
  if (!contacts.length) gaps.push("No contact person recorded");
  if (
    (vendor.gst_treatment === "registered" || vendor.gst_treatment === "composition") &&
    !vendor.gstin
  ) {
    gaps.push("GSTIN missing for a registered vendor");
  }

  return (
    <div className="p-4 md:p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Profile</h2>
          <p className="text-2xs text-muted-foreground">Supplier master used on purchase orders and GRN.</p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/vendors/$vendorId/edit" params={{ vendorId }}>
            <Pencil />
            Edit profile
          </Link>
        </Button>
      </div>

      {gaps.length ? (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-warning/35 bg-warning/8 px-3 py-2">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
          <ul className="text-xs text-muted-foreground">
            {gaps.map((g) => (
              <li key={g}>• {g}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Company" icon={<Building2 />}>
          <Row label="Name" value={vendor.name} />
          <Row label="Code" value={vendor.code} mono />
          <Row label="Type" value={labelFor(VENDOR_TYPES, vendor.vendor_type)} />
          <Row label="Batch prefix" value={vendor.batch_prefix} mono />
          <Row label="GST treatment" value={labelFor(GST_TREATMENTS, vendor.gst_treatment)} />
          <Row label="GSTIN" value={vendor.gstin} mono />
          <Row label="PAN" value={vendor.pan_no} mono />
        </Section>

        <Section title="Reach" icon={<Phone />}>
          <Row
            label="Phone"
            value={
              vendor.work_phone ? (
                <a href={`tel:${vendor.work_phone}`} className="text-primary hover:underline">
                  {vendor.work_phone}
                </a>
              ) : null
            }
          />
          <Row
            label="Email"
            value={
              vendor.email ? (
                <a href={`mailto:${vendor.email}`} className="text-primary hover:underline">
                  {vendor.email}
                </a>
              ) : null
            }
          />
          <Row label="Website" value={vendor.website} />
          {primary ? (
            <Row
              label="Primary contact"
              value={`${primary.name}${primary.mobile ? ` · ${primary.mobile}` : ""}`}
            />
          ) : null}
        </Section>

        <Section title="Address" icon={<MapPin />}>
          <Row label="Address" value={[vendor.addr1, vendor.addr2].filter(Boolean).join(", ")} />
          <Row label="City" value={vendor.city} />
          <Row label="State" value={vendor.state} />
          <Row label="PIN" value={vendor.pincode} />
          <Row label="Place of supply" value={vendor.place_of_supply || vendor.state} />
        </Section>

        <Section title="Commercial" icon={<Mail />}>
          <Row label="Payment terms" value={labelFor(PAYMENT_TERMS, vendor.payment_terms)} />
          <Row label="Credit days" value={String(vendor.credit_days)} />
          <Row label="Credit limit" value={formatMoney(vendor.credit_limit)} />
          <Row
            label="Opening balance"
            value={
              vendor.opening_balance !== 0 || vendor.opening_balance_as_of ? (
                <>
                  {formatMoney(vendor.opening_balance)}
                  {vendor.opening_balance_as_of
                    ? ` as of ${format(new Date(vendor.opening_balance_as_of), "dd MMM yyyy")}`
                    : ""}
                </>
              ) : null
            }
          />
          <Row label="Outstanding" value={formatMoney(vendor.outstanding_balance)} />
          <Row label="Notes" value={vendor.notes} />
          <div className="pt-1">
            <Badge variant={vendor.is_active ? "secondary" : "outline"}>
              {vendor.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <span className="text-muted-foreground [&_svg]:size-3.5">{icon}</span>
        <h3 className="text-xs font-semibold">{title}</h3>
      </header>
      <dl className="space-y-1.5 p-3">{children}</dl>
    </section>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value?: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono truncate" : "truncate"}>{value || "—"}</dd>
    </div>
  );
}
